// The only module that talks to /api/v1. Components use the hooks in
// `resource.ts`, never fetch() directly. Errors are normalised to ApiError
// from the backend's {"error": {code, message, request_id, details}} shape.
import type { AuditEventItem, DocumentDataPoint, DocumentDetail, Dossier, DossierDocument, DocumentType, ChapterRun, Generation, ImportSummary,
  InputReadiness, Job, PeriodRole, QrtFactItem, DatapointsView, ScheduleView, SectionComparison, Contributor, CurrentUser, PlatformUser, Referential, ReferentialVersionItem, ContributorRequestItem, PublicContribution, RaciRole, ReportAudits, ReportPlanView, TeamView, ReportView, ReviewSummary, SectionInputs, SectionReviewState, SectionSources } from "./types";

const BASE = "/api/v1";

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public requestId = "",
              public details: Record<string, unknown> = {}) {
    super(message);
  }
}

function toApiError(status: number, payload: unknown): ApiError {
  const error = (payload as { error?: { code?: string; message?: string; request_id?: string; details?: Record<string, unknown> } })?.error;
  if (error) return new ApiError(status, error.code || "HTTP_ERROR", error.message || `HTTP ${status}`, error.request_id, error.details || {});
  return new ApiError(status, status === 0 ? "NETWORK_ERROR" : "HTTP_ERROR",
    status === 0 ? "The server could not be reached." : `Request failed (HTTP ${status}).`);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, init);
  } catch {
    throw toApiError(0, null);
  }
  const payload = await response.json().catch(() => null);
  if (response.status === 401 && !path.startsWith("/auth/") && !path.startsWith("/contributions/")
      && (payload as { error?: { code?: string } })?.error?.code === "AUTHENTICATION_REQUIRED") {
    // The session ended: sign in again, then come back here.
    window.location.assign(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
  }
  if (!response.ok) throw toApiError(response.status, payload);
  return payload as T;
}

const json = (method: string, body: unknown): RequestInit => ({
  method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
});

export const api = {
  listDossiers: () => request<{ items: Dossier[] }>("/dossiers").then(r => r.items),
  listDeletedDossiers: () => request<{ items: Dossier[] }>("/dossiers?deleted=true").then(r => r.items),
  deleteDossier: (dossierId: string, confirmName: string) =>
    request<Dossier>(`/dossiers/${encodeURIComponent(dossierId)}`, json("DELETE", { confirm_name: confirmName })),
  restoreDossier: (dossierId: string) =>
    request<Dossier>(`/dossiers/${encodeURIComponent(dossierId)}/restore`, { method: "POST" }),
  getDossier: (id: string) => request<Dossier>(`/dossiers/${encodeURIComponent(id)}`),
  createDossier: (body: { name: string; entity_name: string; reporting_year: number; report_type: string }) =>
    request<Dossier>("/dossiers", json("POST", body)),
  listDocuments: (dossierId: string) =>
    request<{ items: DossierDocument[] }>(`/dossiers/${encodeURIComponent(dossierId)}/documents`).then(r => r.items),
  importSummary: (dossierId: string) => request<ImportSummary>(`/dossiers/${encodeURIComponent(dossierId)}/import-summary`),
  inputReadiness: (dossierId: string) => request<InputReadiness>(`/dossiers/${encodeURIComponent(dossierId)}/input-readiness`),
  auditEvents: (dossierId: string) =>
    request<{ items: AuditEventItem[] }>(`/dossiers/${encodeURIComponent(dossierId)}/audit-events?limit=500`).then(r => r.items),
  reportAudits: (dossierId: string, lang: string) =>
    request<ReportAudits>(`/dossiers/${encodeURIComponent(dossierId)}/report-audits?lang=${lang}`),
  reportAuditsExportUrl: (dossierId: string, lang: string) =>
    `${BASE}/dossiers/${encodeURIComponent(dossierId)}/report-audits/export?lang=${lang}`,
  jobs: (dossierId: string) => request<{ items: Job[] }>(`/dossiers/${encodeURIComponent(dossierId)}/jobs`).then(r => r.items),
  sectionSources: (dossierId: string, code: string) =>
    request<SectionSources>(`/dossiers/${encodeURIComponent(dossierId)}/sections/${encodeURIComponent(code)}/sources`),
  updateDossier: (dossierId: string, patch: { name?: string; entity_name?: string; reporting_year?: number }) =>
    request<Dossier>(`/dossiers/${encodeURIComponent(dossierId)}`, json("PATCH", patch)),
  rollover: (dossierId: string, reportingYear: number, name: string) =>
    request<{ dossier: Dossier; document_id: string }>(`/dossiers/${encodeURIComponent(dossierId)}/rollover`,
      json("POST", { reporting_year: reportingYear, name: name || null })),
  datapoints: (dossierId: string, lang: string) =>
    request<DatapointsView>(`/dossiers/${encodeURIComponent(dossierId)}/datapoints?lang=${lang}`),
  datapointsExportUrl: (dossierId: string, lang: string) => `${BASE}/dossiers/${encodeURIComponent(dossierId)}/datapoints/export?lang=${lang}`,
  schedule: (dossierId: string) => request<ScheduleView>(`/dossiers/${encodeURIComponent(dossierId)}/schedule`),
  setDeadline: (dossierId: string, scope: string, dueDate: string | null) =>
    request<ScheduleView>(`/dossiers/${encodeURIComponent(dossierId)}/schedule`, json("PUT", { scope, due_date: dueDate })),
  sectionComparison: (dossierId: string, code: string) =>
    request<SectionComparison>(`/dossiers/${encodeURIComponent(dossierId)}/sections/${encodeURIComponent(code)}/comparison`),
  sectionInputs: (dossierId: string, code: string) =>
    request<SectionInputs>(`/dossiers/${encodeURIComponent(dossierId)}/sections/${encodeURIComponent(code)}/inputs`),
  startGeneration: (dossierId: string, code: string) =>
    request<{ generation: Generation }>(`/dossiers/${encodeURIComponent(dossierId)}/sections/${encodeURIComponent(code)}/generations`, { method: "POST" }),
  generations: (dossierId: string, code: string) =>
    request<{ items: Generation[]; supported: boolean }>(`/dossiers/${encodeURIComponent(dossierId)}/sections/${encodeURIComponent(code)}/generations`),
  startChapter: (dossierId: string, chapter: string) =>
    request<{ chapter_run: ChapterRun }>(`/dossiers/${encodeURIComponent(dossierId)}/chapters/${encodeURIComponent(chapter)}/generations`, { method: "POST" }),
  chapterRuns: (dossierId: string, chapter: string) =>
    request<{ items: ChapterRun[] }>(`/dossiers/${encodeURIComponent(dossierId)}/chapters/${encodeURIComponent(chapter)}/generations`),
  sectionReview: (dossierId: string, code: string) =>
    request<SectionReviewState>(`/dossiers/${encodeURIComponent(dossierId)}/sections/${encodeURIComponent(code)}/review`),
  saveDraft: (dossierId: string, code: string, text: string, basedOn: string) =>
    request<SectionReviewState>(`/dossiers/${encodeURIComponent(dossierId)}/sections/${encodeURIComponent(code)}/draft`,
      json("PUT", { text, based_on: basedOn })),
  restoreApproved: (dossierId: string, code: string, basedOn: string) =>
    request<SectionReviewState>(`/dossiers/${encodeURIComponent(dossierId)}/sections/${encodeURIComponent(code)}/review/restore-approved`,
      json("POST", { based_on: basedOn })),
  reviewSection: (dossierId: string, code: string, decision: "approved" | "changes_requested", contentId: string, comment: string) =>
    request<SectionReviewState>(`/dossiers/${encodeURIComponent(dossierId)}/sections/${encodeURIComponent(code)}/reviews`,
      json("POST", { decision, content_id: contentId, comment: comment || null })),
  report: (dossierId: string, lang: string) => request<ReportView>(`/dossiers/${encodeURIComponent(dossierId)}/report?lang=${lang}`),
  reportPdfUrl: (dossierId: string, lang: string) => `${BASE}/dossiers/${encodeURIComponent(dossierId)}/report/export.pdf?lang=${lang}`,
  reportTemplate: (dossierId: string) =>
    request<{ present: boolean; size?: number; updated_at?: string }>(`/dossiers/${encodeURIComponent(dossierId)}/report/template`),
  uploadReportTemplate: async (dossierId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<{ present: boolean }>(`/dossiers/${encodeURIComponent(dossierId)}/report/template`, { method: "POST", body: form });
  },
  deleteReportTemplate: (dossierId: string) =>
    request<{ present: boolean }>(`/dossiers/${encodeURIComponent(dossierId)}/report/template`, { method: "DELETE" }),
  reportExportUrl: (dossierId: string, lang: string) => `${BASE}/dossiers/${encodeURIComponent(dossierId)}/report/export?lang=${lang}`,
  plan: (dossierId: string, lang: string) => request<ReportPlanView>(`/dossiers/${encodeURIComponent(dossierId)}/plan?lang=${lang}`),
  decidePlanItem: (dossierId: string, itemId: string, decision: "accepted" | "rejected") =>
    request<ReportPlanView>(`/dossiers/${encodeURIComponent(dossierId)}/plan/decisions`, json("POST", { item_id: itemId, decision })),
  validatePlan: (dossierId: string) =>
    request<ReportPlanView>(`/dossiers/${encodeURIComponent(dossierId)}/plan/validate`, { method: "POST" }),
  team: (dossierId: string) => request<TeamView>(`/dossiers/${encodeURIComponent(dossierId)}/team`),
  addContributor: (dossierId: string, body: { name: string; email: string; role: string; department: string }) =>
    request<Contributor>(`/dossiers/${encodeURIComponent(dossierId)}/team/contributors`, json("POST", body)),
  setContributorActive: (dossierId: string, contributorId: string, active: boolean) =>
    request<Contributor>(`/dossiers/${encodeURIComponent(dossierId)}/team/contributors/${encodeURIComponent(contributorId)}`, json("PATCH", { active })),
  setRaci: (dossierId: string, scope: string, role: RaciRole, contributorIds: string[]) =>
    request<TeamView>(`/dossiers/${encodeURIComponent(dossierId)}/team/raci`, json("PUT", { scope, role, contributor_ids: contributorIds })),
  sectionRequests: (dossierId: string, code: string) =>
    request<{ items: ContributorRequestItem[]; responsible: Contributor | null }>(`/dossiers/${encodeURIComponent(dossierId)}/sections/${encodeURIComponent(code)}/requests`),
  createRequest: (dossierId: string, code: string, body: { question: string; gap: string | null; contributor_id: string | null }) =>
    request<{ request: ContributorRequestItem; link_path: string; emailed: boolean; mail_configured: boolean }>(`/dossiers/${encodeURIComponent(dossierId)}/sections/${encodeURIComponent(code)}/requests`, json("POST", body)),
  cancelRequest: (dossierId: string, requestId: string) =>
    request<ContributorRequestItem>(`/dossiers/${encodeURIComponent(dossierId)}/requests/${encodeURIComponent(requestId)}/cancel`, { method: "POST" }),
  contribution: (token: string) => request<PublicContribution>(`/contributions/${encodeURIComponent(token)}`),
  answerContribution: (token: string, answer: string) =>
    request<PublicContribution>(`/contributions/${encodeURIComponent(token)}`, json("POST", { answer })),
  referential: () => request<Referential>("/referential"),
  proposeReferential: (body: { kind: string; key: string; value: string; reason: string }) =>
    request<ReferentialVersionItem>("/referential/proposals", json("POST", body)),
  decideReferential: (id: string, decision: "approved" | "rejected", comment: string) =>
    request<ReferentialVersionItem>(`/referential/proposals/${encodeURIComponent(id)}/decision`, json("POST", { decision, comment: comment || null })),
  referentialHistory: (kind: string, key: string) =>
    request<{ items: ReferentialVersionItem[] }>(`/referential/history?kind=${encodeURIComponent(kind)}&key=${encodeURIComponent(key)}`),
  me: () => request<CurrentUser>("/auth/me"),
  login: (email: string, password: string) => request<CurrentUser>("/auth/login", json("POST", { email, password })),
  logout: () => request<{ signed_out: boolean }>("/auth/logout", { method: "POST" }),
  users: () => request<{ items: PlatformUser[] }>("/auth/users"),
  createUser: (body: { name: string; email: string; role: string; password: string }) =>
    request<PlatformUser>("/auth/users", json("POST", body)),
  changePassword: (current: string, next: string) => request<{ changed: boolean }>("/auth/password", json("POST", { current, new: next })),
  updateUser: (id: string, changes: { role?: string; active?: boolean; password?: string }) =>
    request<PlatformUser>(`/auth/users/${encodeURIComponent(id)}`, json("PATCH", changes)),
  reviewSummary: (dossierId: string) => request<ReviewSummary>(`/dossiers/${encodeURIComponent(dossierId)}/reviews`),
  generationSummary: (dossierId: string) =>
    request<{ supported: string[]; items: Generation[] }>(`/dossiers/${encodeURIComponent(dossierId)}/generations`),
  getDocument: (id: string) => request<DocumentDetail>(`/documents/${encodeURIComponent(id)}`),
  documentDataPoints: (id: string) =>
    request<{ document_id: string; total: number; items: DocumentDataPoint[] }>(`/documents/${encodeURIComponent(id)}/data-points`),
  correctDocument: (id: string, changes: { document_type?: DocumentType; reporting_year?: number | null;
    period_role?: PeriodRole; entity_name?: string; qrt_unit?: "EUR" | "kEUR" | "MEUR" }) => request<DossierDocument>(`/documents/${encodeURIComponent(id)}`, json("PATCH", changes)),
  removeDocument: (id: string) => request<{ id: string }>(`/documents/${encodeURIComponent(id)}`, { method: "DELETE" }),
  restoreDocument: (id: string) => request<DossierDocument>(`/documents/${encodeURIComponent(id)}/restore`, { method: "POST" }),
  reprocess: (id: string) => request<{ job: Job }>(`/documents/${encodeURIComponent(id)}/reprocess`, { method: "POST" }),
  qrtFacts: (id: string, template: string, limit = 500) =>
    request<{ total: number; items: QrtFactItem[] }>(`/documents/${encodeURIComponent(id)}/qrt-facts?template=${encodeURIComponent(template)}&limit=${limit}`),
  fileUrl: (id: string, page?: number | null) => `${BASE}/documents/${encodeURIComponent(id)}/file${page ? `#page=${page}` : ""}`,
};

export interface UploadHandle {
  done: Promise<{ document: DossierDocument; job: Job }>;
  cancel: () => void;
}

type Sent = { status: number; payload: unknown };

// XHR rather than fetch: only XHR reports upload progress.
function send(request: XMLHttpRequest, method: string, url: string, body: XMLHttpRequestBodyInit | null,
              headers: Record<string, string> = {}, onProgress?: (percent: number) => void): Promise<Sent> {
  return new Promise((resolve, reject) => {
    request.open(method, url);
    for (const [name, value] of Object.entries(headers)) request.setRequestHeader(name, value);
    if (onProgress) request.upload.addEventListener("progress", event => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    });
    request.addEventListener("load", () => {
      let payload: unknown = null;
      try { payload = JSON.parse(request.responseText); } catch { /* no JSON body */ }
      resolve({ status: request.status, payload });
    });
    request.addEventListener("error", () => reject(toApiError(0, null)));
    request.addEventListener("abort", () => reject(new ApiError(0, "UPLOAD_CANCELLED", "Upload cancelled.")));
    request.send(body);
  });
}

type Uploaded = { document: DossierDocument; job: Job };
type UploadTarget = { mode: "form" } | { mode: "direct"; upload_id: string; url: string; max_bytes: number };

/** Upload one file to `path` (a dossier's documents or a document's versions).
 * When the server stores files in object storage, the file goes straight to a
 * signed storage URL (no platform request-size limit) and the API then imports
 * it; otherwise it is posted to the API as a form. */
export function uploadFile(path: string, file: File, onProgress: (percent: number) => void): UploadHandle {
  let current: XMLHttpRequest | null = null;
  let cancelled = false;
  const step = (method: string, url: string, body: XMLHttpRequestBodyInit | null, headers?: Record<string, string>,
                progress?: (percent: number) => void) => {
    if (cancelled) return Promise.reject(new ApiError(0, "UPLOAD_CANCELLED", "Upload cancelled."));
    current = new XMLHttpRequest();
    return send(current, method, url, body, headers, progress);
  };
  const ok = ({ status, payload }: Sent) => {
    if (status >= 200 && status < 300) return payload;
    throw toApiError(status, payload);
  };
  const done = (async (): Promise<Uploaded> => {
    const target = ok(await step("POST", `${BASE}${path}/upload-url`, null)) as UploadTarget;
    if (target.mode === "direct") {
      if (file.size > target.max_bytes) throw new ApiError(413, "FILE_TOO_LARGE",
        `The file exceeds ${Math.round(target.max_bytes / 1_000_000)} MB.`);
      const put = await step("PUT", target.url, file, { "Content-Type": file.type || "application/octet-stream" }, onProgress);
      if (put.status < 200 || put.status >= 300) throw new ApiError(put.status, "STORAGE_UPLOAD_FAILED",
        "The file could not be sent to storage. Retry.");
      return ok(await step("POST", `${BASE}${path}/from-upload`, JSON.stringify({ upload_id: target.upload_id, filename: file.name }),
        { "Content-Type": "application/json" })) as Uploaded;
    }
    const body = new FormData();
    body.append("file", file);
    return ok(await step("POST", `${BASE}${path}`, body, {}, onProgress)) as Uploaded;
  })();
  return { done, cancel: () => { cancelled = true; current?.abort(); } };
}

export const uploadToDossier = (dossierId: string, file: File, onProgress: (p: number) => void) =>
  uploadFile(`/dossiers/${encodeURIComponent(dossierId)}/documents`, file, onProgress);
export const uploadNewVersion = (documentId: string, file: File, onProgress: (p: number) => void) =>
  uploadFile(`/documents/${encodeURIComponent(documentId)}/versions`, file, onProgress);
